/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { useContext, useEffect, useRef, useState } from "react";
import { getHTMLFontFamily, isMarkupAnnotNode } from "utils/AccessibilityUtils";
import { FormattedCopyContext } from "utils/CopyTextUtils";
import { AccessibleLinkNodeContainer } from "containers/accessibility/AccessibleLinkNodeContainer";
import { AccessibleMarkupCommentNodeContainer } from "containers/accessibility/AccessibleMarkupCommentNodeContainer";
import { AccessibleHeadingNode } from "components/accessibility/AccessibleHeadingNode";
import { AccessibleListNode } from "components/accessibility/AccessibleListNode";
import { AccessibleFormNode } from "components/accessibility/AccessibleFormNode";
import { AccessibleTextNode } from "components/accessibility/AccessibleTextNode";
import { AccessibleGraphicNode } from "components/accessibility/AccessibleGraphicNode";
import { AccessibleTableNode } from "components/accessibility/AccessibleTableNode";
import { AccessibleParagraphNode } from "components/accessibility/AccessibleParagraphNode";
import { shouldFetchAccContent } from "selectors/accessibilitySelectors";
import { getShouldRenderAcroForm, getViewRotationAngle } from "selectors/documentSelectors";
import { getPageRotation } from "selectors/pageSelectors";
import { getPositionFromRect } from "utils/ReadAloudUtils";
import { ACCESS_LAYER_SCALEX_THRESHOLD, ACCESS_LAYER_SCALEY_THRESHOLD } from "constants/constants";
import { isCopyWithFormattingEnabled } from "selectors/appSelectors";
import * as stores from "stores";

export const getRelativeNodePosition = (childRect, parentRect, pageIndex) => {
  const { top, bottom, left, right } = getPositionFromRect(childRect, pageIndex, stores);
  const { top: parentTop, left: parentLeft } = parentRect ? getPositionFromRect(parentRect, pageIndex, stores) : {};
  return {
    position: "absolute",
    left: `${left - (parentLeft || 0)}px`,
    top: `${top - (parentTop || 0)}px`,
    width: `${Math.abs(right - left)}px`,
    height: `${Math.abs(bottom - top)}px`,
    fontSize: `0px`
  };
};

export const AccessibleSpan = ({ style, spanContent, key, targetWidth, rotation, height, width }) => {
  const ref = useRef(null);
  const formattedCopyContext = useContext(FormattedCopyContext);
  const [scaleX, setScaleX] = useState(1);
  const [scaleY, setScaleY] = useState(1);

  // If used in accessibility layer, keep text transparent (hidden). For copy with formatting, keep the original color.
  if (!formattedCopyContext) style.color = "transparent";

  useEffect(() => {
    if (rotation === 0 || rotation === 180) {
      const scalex = targetWidth / ref.current.offsetWidth;
      if (scalex <= ACCESS_LAYER_SCALEX_THRESHOLD) setScaleX(scalex);
    } else {
      const scaley = targetWidth / ref.current.offsetHeight;
      if (scaley <= ACCESS_LAYER_SCALEY_THRESHOLD) setScaleY(scaley);
    }
  }, [rotation]);

  switch (rotation) {
    case 0:
      style.lineHeight = height;
      style.width = undefined;
      style.transform = `scaleX(${scaleX})`;
      style.transformOrigin = "left center";
      break;

    case 90:
      style.writingMode = "vertical-lr";
      style.lineHeight = width;
      style.height = undefined;
      style.transform = `scaleY(${scaleY})`;
      style.transformOrigin = "top center";
      break;

    case 180:
      style.lineHeight = height;
      style.width = undefined;
      style.transform = `scaleX(${scaleX})` + " rotate(180deg)";
      style.marginLeft = `${(targetWidth - targetWidth / scaleX) / 2}px`;
      break;

    case 270:
      style.writingMode = "vertical-lr";
      style.height = undefined;
      style.lineHeight = width;
      style.transform = `scaleY(${scaleY})` + " rotate(180deg)";
      style.marginTop = `${(targetWidth - targetWidth / scaleY) / 2}px`;
      break;
  }

  const newStyle = { ...style };
  return (
    <span ref={ref} style={newStyle} key={key}>
      {spanContent}
    </span>
  );
};
const colorIsNotBlack = rgbaColor => rgbaColor && (rgbaColor[0] !== 0 || rgbaColor[1] !== 0 || rgbaColor[2] !== 0);

const getTextRunToRender = (textNode, pageIndex, content, idx, parentRect, zoomLevel) => {
  const spanContent = content.substring(textNode.startCharIdx, textNode.startCharIdx + textNode.charCnt);
  const baseStyle = getRelativeNodePosition(textNode.rect, parentRect, pageIndex);
  const nodeFontStyle = textNode.isItalics ? "italic" : "normal";
  const fontColor = colorIsNotBlack(textNode.strokeColor) ? textNode.strokeColor : textNode.fillColor;
  const rgbaColor =
    fontColor && isCopyWithFormattingEnabled(stores)
      ? `rgba(${fontColor[0] * 255},${fontColor[1] * 255}, ${fontColor[2] * 255}, ${fontColor[3]})`
      : "transparent";
  const fontFamilyStr = getHTMLFontFamily(textNode.fontFamily);
  // styles calculated for each accessibility span
  const style = {
    ...baseStyle,
    fontSize: `${textNode.fontSize * zoomLevel}px`, // assuming T5 gives fontSize w.r.t. zoomLevel 1
    fontWeight: textNode.fontWeight,
    fontFamily: fontFamilyStr,
    whiteSpace: "pre",
    fontStyle: nodeFontStyle,
    color: rgbaColor,
    forcedColorAdjust: "none", // to handle high contrast
    letterSpacing: `${textNode.charSpace * zoomLevel}px` // assuming T5 gives charSpace w.r.t. zoomLevel 1
  };

  const rotation = (getViewRotationAngle(stores) + getPageRotation(stores, pageIndex)) % 360;
  let t5Width = Math.abs(textNode.rect.xMax - textNode.rect.xMin) * zoomLevel;
  let t5Height = Math.abs(textNode.rect.yMax - textNode.rect.yMin) * zoomLevel;

  // on pageRotation, we receive height and width as per rotation angle,
  // but that's not the case with viewRotation.
  // Interchanging width and height for pageRotation to use
  // same roation logic on page and view roation
  if (getPageRotation(stores, pageIndex) === 90 || getPageRotation(stores, pageIndex) === 270) {
    [t5Width, t5Height] = [t5Height, t5Width];
  }
  return (
    <AccessibleSpan
      style={style}
      spanContent={spanContent}
      key={idx}
      targetWidth={t5Width}
      rotation={rotation}
      height={baseStyle.height}
      width={baseStyle.width}
    />
  );
};

const getTextRunsToRender = (textAttributeNodes, pageIndex, content, parentRect, zoomLevel) =>
  textAttributeNodes &&
  textAttributeNodes.map((node, idx) => getTextRunToRender(node, pageIndex, content, idx, parentRect, zoomLevel));

export const getTextContentToRender = (textAttributeNodes, pageIndex, content, parentRect, zoomLevel) => {
  if (textAttributeNodes && textAttributeNodes.length > 0) {
    return getTextRunsToRender(textAttributeNodes, pageIndex, content, parentRect, zoomLevel);
  }
  return content;
};

export const getAccessibleComponentToRender = (node, zoomLevel, index, pageIndex, parentNode) => {
  const style = getRelativeNodePosition(node.contentRect, parentNode && parentNode.contentRect, pageIndex);
  const textContentToRender = getTextContentToRender(
    node.textAttributeNodes,
    pageIndex,
    node.contentDesc,
    node.contentRect,
    zoomLevel
  );
  switch (node.contentRole) {
    case "heading":
      return (
        node.info &&
        node.info.headingLevel !== undefined && (
          <AccessibleHeadingNode
            level={node.info.headingLevel}
            style={style}
            key={index}
            textContent={textContentToRender}
          />
        )
      );
    case "link":
      return (
        <AccessibleLinkNodeContainer
          style={style}
          pageIndex={pageIndex}
          nodeId={node.id}
          key={index}
          textContent={textContentToRender}
          contentRect={node.contentRect}
          linkInfo={node.info}
        />
      );
    case "graphic":
      return <AccessibleGraphicNode style={style} altText={node.contentDesc} key={index} />;
    case "list":
      return <AccessibleListNode style={style} list={node} key={index} zoomLevel={zoomLevel} pageIndex={pageIndex} />;
    case "annotation":
      return (
        shouldFetchAccContent(stores) &&
        isMarkupAnnotNode(node) && (
          <AccessibleMarkupCommentNodeContainer
            pageIndex={pageIndex}
            nodeId={node.id}
            annotInfo={node.info}
            key={index}
            style={style}
            textContent={textContentToRender}
            contentRect={node.contentRect}
          />
        )
      );
    case "form":
      return (
        shouldFetchAccContent(stores) && (
          <AccessibleFormNode
            key={index}
            style={style}
            parentRect={parentNode && getPositionFromRect(parentNode.contentRect, pageIndex, stores)}
            annotInfo={node.info}
            shouldRenderReadonlyFields={!getShouldRenderAcroForm(stores)}
          />
        )
      );
    case "table":
      return <AccessibleTableNode style={style} table={node} key={index} zoomLevel={zoomLevel} pageIndex={pageIndex} />;
    case "paragraph":
      return (
        <AccessibleParagraphNode
          style={style}
          paragraph={node}
          key={index}
          zoomLevel={zoomLevel}
          pageIndex={pageIndex}
        />
      );
    case "statictext":
      return <AccessibleTextNode style={style} key={index} pageIndex={pageIndex} textContent={textContentToRender} />;
    default:
  }
  return null;
};
